import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {Injectable, InjectionToken, Injector, OnDestroy} from "@angular/core";
import {LoadingBarService} from "@synisys/idm-ui-shell/app/services/loading-bar.service";
import {Observable} from "rxjs/Observable";
import {finalize} from "rxjs/operators";
import {Subscription} from "rxjs/Subscription";
import {SharedSubjectModel} from "../shared/model/shared-subject.model";

export const EXCLUDED_FOR_LOADING_URLS = new InjectionToken<string[]>("EXCLUDED URLS FOR LOADING INTERCEPTOR");

@Injectable()
export class PrimeLoadingInterceptor implements HttpInterceptor, OnDestroy {

  private requestCount = 0;
  private excludedUrls: string[];
  private navigationRequestCount = 0;
  private subscriptions: Subscription;

  constructor(
    private injector: Injector,
    private loadingBarService: LoadingBarService,
    private sharedSubjectModel: SharedSubjectModel
  ) {
    this.excludedUrls = injector.get<string[]>(EXCLUDED_FOR_LOADING_URLS, []);

    // removed "navigate" which was used early
    this.excludedUrls.push("notifications/count", "classifiers/modification", "data-import-state", "comment/count", "token/prolong", "metrics/collect");
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // If is navigation request, gets the navigation category and sends to MetricsServiceImpl
    // catching navigation events in this way, because navigation events from Router didn't want to work
    // even when using the interceptor from loading-interceptor of idm-ui-shell.

    const isNavigationRequest = this.isNavigateRequest(request);
    if (isNavigationRequest) {
      const category = this.getNavigationRequestCategory(request);

      if (category) {
        this.sharedSubjectModel.categorySubject.next(category);
      }
    }

    const ignore = this.excluded(request.url);
    if (ignore) {
      return next.handle(request);
    }

    this.start();
    // finalize() ends the request exactly once on any terminal state
    // (response, error or cancellation), keeping the in-flight counter accurate.
    return next.handle(request).pipe(finalize(() => this.end()));
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  private start(): void {
    if (this.requestCount === 0) {
      this.loadingBarService.start();

    }
    this.requestCount++;
  }

  private end(): void {
    if (this.requestCount > 0) {
      this.requestCount--;
    }

    if (this.requestCount === 0 && this.navigationRequestCount === 0) {
      this.loadingBarService.stop();
    }
  }

  private excluded(url: string): boolean {
    return this.excludedUrls.some((ignoreUrl) => url.endsWith(ignoreUrl));
  }

  private isNavigateRequest(request: HttpRequest<any>): boolean {
    return request.url === "navigate" && request.method === "POST";
  }

  private getNavigationRequestCategory(request: any): string {
    const url = request.body.url;

    switch (true) {

      // Portfolio
      case url === "/hr-register":
        return "HR Register Portfolio";
      case url === "/awards":
        return "Award Portfolio";
      case url === "/actions":
        return "Action Portfolio";
      case url === "/partner-register":
        return "Partner Portfolio";

      // Project
      case url === "/projects":
        return "Project Portfolio";
      case url.includes("/ProjectView"):
        return "Project View";
      case url.includes("/ProjectEdit"):
        return "Project Edit";
      case url.includes("/ProjectOverview"):
        return "Project Overview";

      // Log Frame
      case url.includes("/LogframeView"):
        return "Log Frame View";
      case url.includes("/LogframeEdit"):
        return "Log Frame Edit";

      // Dip
      case url.includes("/DipView"):
        return "Dip View";
      case url.includes("/DipEdit"):
        return "Dip Edit";

      // HR Management
      case url.includes("/HRManagementView"):
        return "HR Management";

      // Strategic Goal Link
      case url.includes("/StrategicGoalLinkView"):
        return "Strategic Goal Link";

      // MEAL Plan
      case url.includes("/MEALPlanView"):
        return "MEAL Plan View";
      case url.includes("/MEALPlanEdit"):
        return "MEAL Plan Edit";

      // Action Management
      case url.includes("/ActionManagementView"):
        return "Action Management";

      // Partner Management
      case url.includes("/PartnerManagementView"):
        return "Project Partner Management";

      // Budget Holder
      case url.includes("/BudgetHolderView"):
        return "Budget Holder View";
      case url.includes("/BudgetHolderEdit"):
        return "Budget Holder Edit";

      // People and Groups
      case url.includes("/BeneficiaryView"):
        return "Project People and Groups";

      // Individual
      case url === "/beneficiaries/individuals":
        return "Individual Portfolio";
      case url.includes("/Individual/IndividualView"):
        return "Individual View";
      case url.includes("/Individual/IndividualEdit"):
        return "Individual Edit";

      // Household
      case url === "/beneficiaries/households":
        return "Household Portfolio";
      case url.includes("/Household/HouseholdViewForm"):
        return "Household View";
      case url.includes("/Household/HouseholdEdit"):
        return "Household Edit";

      // Association
      case url === "/beneficiaries/associations":
        return "Association Portfolio";
      case url.includes("/Association/Association"):
        return "Association View";
      case url.includes("/Association/AssociationEdit"):
        return "Association Edit";

      // Community
      case url === "/beneficiaries/communities":
        return "Community Portfolio";
      case url.includes("/Community/CommunityViewForm"):
        return "Community View";
      case url.includes("/Community/CommunityEdit"):
        return "Community Edit";

      // Indicator
      case url === "/me/indicator/portfolio":
        return "Indicator Portfolio";
      case url.includes("/IndicatorView"):
        return "Indicator View";
      case url.includes("/IndicatorEdit"):
        return "Indicator Edit";

      default:
        return "Other";
    }
  }

}
